import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ValidationIssue } from '../core/model/issues.ts';
import { en, enHints, enIssues, type IssueMessages, type MessageKey, type Messages } from './messages.ts';
import { de, deHints, deIssues } from './locales/de.ts';
import { pt, ptHints, ptIssues } from './locales/pt.ts';

export const LOCALES = ['en', 'pt', 'de'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  pt: 'Português',
  de: 'Deutsch',
};

/**
 * The full tag each interface language actually is.
 *
 * `en-GB` rather than `en-US` because that is what the copy is: this interface says "Licence" and
 * "colour". Labelling it American would be a small lie told on every screen.
 */
export const LOCALE_TAGS: Record<Locale, string> = {
  en: 'en-GB',
  pt: 'pt-BR',
  de: 'de-DE',
};

interface Bundle {
  readonly messages: Messages;
  readonly issues: IssueMessages;
  readonly hints: Record<string, string>;
}

const BUNDLES: Record<Locale, Bundle> = {
  en: { messages: en, issues: enIssues, hints: enHints },
  pt: { messages: pt, issues: ptIssues, hints: ptHints },
  de: { messages: de, issues: deIssues, hints: deHints },
};

export type Params = Readonly<Record<string, string | number>>;

/**
 * Substitutes `{name}` placeholders.
 *
 * A missing placeholder is left as it is rather than blanked: a visible `{count}` is a bug someone
 * reports, while a silently empty sentence is one that survives for years.
 */
export function interpolate(template: string, params: Params = {}): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

export interface Translator {
  readonly locale: Locale;
  /** A user-interface string. */
  readonly t: (key: MessageKey, params?: Params) => string;
  /** The sentence for a diagnostic, rendered from its code and data. */
  readonly issue: (issue: ValidationIssue) => string;
  /** The actionable suggestion attached to a diagnostic, if it has one. */
  readonly hint: (issue: ValidationIssue) => string | undefined;
  readonly setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<Translator | null>(null);

/**
 * Picks a starting language from the browser, falling back to English.
 *
 * The interface language and the language of the exam content are deliberately independent: someone
 * studying for an English certification in Brazil wants a Portuguese interface around English
 * questions, and the screen reader has to be told which is which.
 */
export function detectLocale(languages: readonly string[] = navigator.languages): Locale {
  for (const candidate of languages) {
    const base = candidate.toLowerCase().split('-')[0];
    const match = LOCALES.find((locale) => locale === base);
    if (match !== undefined) {
      return match;
    }
  }
  return 'en';
}

export function createTranslator(locale: Locale, setLocale: (next: Locale) => void): Translator {
  const bundle = BUNDLES[locale];
  const fallback = BUNDLES.en;

  return {
    locale,
    setLocale,
    t: (key, params) => interpolate(bundle.messages[key], params),
    issue: (candidate) => {
      const template = bundle.issues[candidate.code];
      return interpolate(template, {
        ...candidate.params,
        ...(candidate.expected !== undefined && { expected: candidate.expected }),
        ...(candidate.received !== undefined && { received: candidate.received }),
        ...(candidate.field !== undefined && { field: candidate.field }),
      });
    },
    hint: (candidate) => {
      if (candidate.hint === undefined) {
        return undefined;
      }
      const template = bundle.hints[candidate.hint] ?? fallback.hints[candidate.hint];
      return template === undefined ? undefined : interpolate(template, candidate.params);
    },
  };
}

export function I18nProvider({
  children,
  initialLocale,
}: {
  readonly children: ReactNode;
  readonly initialLocale?: Locale | undefined;
}) {
  const [locale, setLocale] = useState<Locale>(initialLocale ?? detectLocale());

  const value = useMemo(() => createTranslator(locale, setLocale), [locale]);

  // The document language follows the interface, never the exam; content carries its own `lang`.
  // In an effect rather than in render: touching the DOM while rendering is a side effect, and
  // under StrictMode's double render it would run twice.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): Translator {
  const value = useContext(I18nContext);
  if (value === null) {
    throw new Error('useI18n must be used inside an I18nProvider');
  }
  return value;
}

export type { MessageKey, Messages };
